import { clsx } from 'clsx';
import * as styles from './media.css';

const supportedImageTypes = ['jpg', 'jpeg', 'png', 'gif'];
const supportedVideoTypes = ['mp4', 'm4v', 'webm', 'mov'];

type MediaViewerMetadata = {
  cdn01?: string;
  image?: string;
  video?: string;
};

/**
 * Which media shape the viewer is previewing. The legacy stylesheet drove this
 * off a shared class name on the media box; it is now an explicit prop so the
 * sizes can live on the elements they apply to.
 *
 * The variant only selects the inner tile / video size. The viewer's own frame
 * is 245x245 for both, because every call site sits inside the square media box
 * and that box sized its descendants - see `viewerInSquareBox`.
 */
export type MediaVariant = 'square' | 'vertical';

type MediaViewerProps = {
  metadata?: MediaViewerMetadata | null;
  variant?: MediaVariant;
  width?: string;
  height?: string;
};

const getExtension = (path?: string): string =>
  path ? (path.split('.').pop()?.toLowerCase() ?? '') : '';

const MediaViewer = ({
  metadata,
  variant,
  width,
  height,
}: MediaViewerProps) => {
  const viewerClass = clsx(styles.viewer, variant && styles.viewerInSquareBox);
  const previewClass = clsx(
    styles.preview,
    variant === 'square' && styles.previewSquare,
    variant === 'vertical' && styles.previewVertical,
  );

  if (!metadata) {
    return (
      <div className={viewerClass}>
        <div className={previewClass}>No media specified.</div>
      </div>
    );
  }

  const linkExtension = getExtension(metadata.video ?? metadata.image);
  const imageLink = `${metadata.cdn01 ?? ''}${metadata.image ?? ''}`;
  const videoLink = `${metadata.cdn01 ?? ''}${metadata.video ?? ''}`;

  if (supportedImageTypes.includes(linkExtension)) {
    return (
      <div className={viewerClass} style={{ width, height }}>
        <a target="_blank" rel="noreferrer" href={imageLink}>
          <div
            className={previewClass}
            style={{
              backgroundImage: `url("${imageLink}")`,
              width,
              height,
            }}
          />
        </a>
      </div>
    );
  }

  if (supportedVideoTypes.includes(linkExtension)) {
    return (
      <div className={viewerClass}>
        <a target="_blank" rel="noreferrer" href={videoLink}>
          <video
            className={clsx(
              styles.video,
              variant === 'vertical' && styles.videoVertical,
            )}
            preload="auto"
            controls
          >
            <source src={videoLink} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </a>
      </div>
    );
  }

  // user published something weird or old here, try to adapt
  const link = metadata.video ?? metadata.image ?? '';

  return (
    <div className={viewerClass}>
      <a target="_blank" rel="noreferrer" href={link}>
        <div className={previewClass}>No preview available.</div>
      </a>
    </div>
  );
};

export default MediaViewer;
